<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-container glass-modal-container modal-dispute">
      <div class="modal-header glass-modal-header">
        <h3>Edit Dispute #{{ dispute ? dispute.invoice_id : '' }}</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <!-- Debug info (set debugMode to false after testing) -->
        <div v-if="debugMode" style="background: #f8f9fa; padding: 10px; margin-bottom: 15px; border-radius: 8px; font-size: 12px; max-height: 300px; overflow-y: auto;">
          <strong>Debug:</strong>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Dispute ID: {{ dispute ? dispute.id : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Dispute Type: {{ dispute ? dispute.dispute_type : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Money Lost: {{ dispute ? dispute.money_lost : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Money Amount: {{ dispute ? dispute.money_amount : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Dispute Company Name: {{ dispute ? dispute.dispute_company_name : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Credit Card Company: {{ dispute ? dispute.credit_card_company : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Documents: {{ dispute ? dispute.documents.length : 'null' }}</pre>
          <pre style="margin: 5px 0; white-space: pre-wrap;">Form Data: {{ JSON.stringify(form, null, 2) }}</pre>
        </div>

        <div class="form-group">
          <label>Dispute Type *</label>
          <select v-model="form.dispute_type" @change="onDisputeTypeChange" class="modal-select glass-select">
            <option :value="null">Select dispute type</option>
            <option value="customer">Customer Dispute</option>
            <option value="credit_card">Credit Card Company</option>
            <option value="snap_finance">Finance Company</option>
            <option value="merchant_service">Merchant Service</option>
            <option value="attorney">Attorney</option>
            <option value="court">Court</option>
          </select>
        </div>
        
        <div class="form-group" v-if="form.dispute_type === 'credit_card'">
          <label>Credit Card Company *</label>
          <input type="text" v-model="form.credit_card_company" class="modal-input glass-input" placeholder="e.g., Visa, Mastercard" />
          <p v-if="form.credit_card_company" style="font-size: 12px; color: #10b981; margin-top: 4px;">
            ✓ Current: {{ form.credit_card_company }}
          </p>
        </div>
        
        <div class="form-group" v-if="isFinanceType && form.dispute_type !== 'credit_card'">
          <label>{{ companyLabel }} *</label>
          <input type="text" v-model="form.dispute_company_name" class="modal-input glass-input" :placeholder="companyPlaceholder" />
          <p v-if="form.dispute_company_name" style="font-size: 12px; color: #10b981; margin-top: 4px;">
            ✓ Current: {{ form.dispute_company_name }}
          </p>
        </div>
        
        <div class="form-group">
          <label>Customer Statement *</label>
          <div class="radio-group">
            <label class="radio-label glass-radio">
              <input type="radio" value="received" v-model="form.customer_statement" /> Received
            </label>
            <label class="radio-label glass-radio">
              <input type="radio" value="not_received" v-model="form.customer_statement" /> Not Received
            </label>
          </div>
        </div>
        
        <div class="form-group">
          <label>Issue Type *</label>
          <select v-model="form.issue_type" class="modal-select glass-select">
            <option :value="null">Select issue type</option>
            <option value="fraud_charges">Fraud Charges</option>
            <option value="broken_merchandise">Broken Merchandise</option>
            <option value="wrong_item">Wrong Item</option>
            <option value="missing_item">Missing Item</option>
            <option value="not_as_described">Not as Described</option>
            <option value="delivery_issue">Delivery Issue</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Resolution Status</label>
          <select v-model="form.resolution" class="modal-select glass-select">
            <option value="pending">Pending</option>
            <option value="customer_favor">Customer's Favor</option>
            <option value="business_favor">Business's Favor</option>
            <option value="partial_refund">Partial Refund</option>
            <option value="full_refund">Full Refund</option>
            <option value="replacement">Replacement</option>
            <option value="chargeback_won">Chargeback Won</option>
            <option value="chargeback_lost">Chargeback Lost</option>
            <option value="legal_action">Legal Action</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Money Lost?</label>
          <div class="radio-group">
            <label class="radio-label glass-radio">
              <input type="radio" :value="true" v-model="form.money_lost" /> Yes
            </label>
            <label class="radio-label glass-radio">
              <input type="radio" :value="false" v-model="form.money_lost" /> No
            </label>
          </div>
        </div>
        
        <!-- Money Amount Field -->
        <div class="form-group" v-if="form.money_lost">
          <label>Money Lost Amount *</label>
          <div class="money-input-wrapper">
            <span class="currency-symbol">$</span>
            <input 
              type="number" 
              v-model="form.money_amount" 
              step="0.01" 
              min="0.01"
              class="modal-input glass-input money-amount-input" 
              placeholder="0.00"
            />
          </div>
          <p class="field-hint">Please enter the exact amount of money lost (e.g., 49.99)</p>
        </div>
        
        <div class="form-group">
          <label>Dispute Details</label>
          <textarea v-model="form.details" rows="3" class="modal-textarea glass-input" placeholder="Enter any additional details..."></textarea>
        </div>

        <!-- Document Upload Section -->
        <div class="form-group document-upload-section">
          <label>Documents & Evidence</label>
          
          <!-- Existing Documents -->
          <div v-if="existingDocuments.length > 0" class="existing-documents">
            <p class="documents-label">Current Documents ({{ existingDocuments.length }}):</p>
            <div class="documents-list">
              <div v-for="(doc, index) in existingDocuments" :key="doc.id || index" class="document-item glass-doc-item">
                <div class="doc-info">
                  <i class="fas fa-file-alt doc-icon"></i>
                  <span class="doc-name">{{ doc.title || 'Untitled' }}</span>
                  <span class="doc-type">{{ doc.document_type || 'Document' }}</span>
                </div>
                <div class="doc-actions">
                  <button @click="viewDocument(doc)" class="doc-action-btn view-btn" title="View document">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button @click="removeDocument(index)" class="doc-action-btn remove-btn" title="Remove document">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Add New Documents -->
          <div class="add-document-section">
            <p class="documents-label">Add New Documents:</p>
            <div class="document-upload-row">
              <input 
                type="text" 
                v-model="newDocument.title" 
                class="modal-input glass-input doc-title-input" 
                placeholder="Document title..."
              />
              <select v-model="newDocument.document_type" class="modal-select glass-select doc-type-select">
                <option value="">Select type</option>
                <option value="evidence">Evidence</option>
                <option value="statement">Statement</option>
                <option value="contract">Contract</option>
                <option value="communication">Communication</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="document-upload-row">
              <input 
                type="file" 
                @change="handleDocumentUpload" 
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                class="file-input"
                ref="fileInput"
              />
              <button 
                @click="addDocument" 
                class="add-doc-btn glass-btn-secondary"
                :disabled="!newDocument.file || !newDocument.title"
              >
                <i class="fas fa-plus"></i> Add Document
              </button>
            </div>
            <p v-if="newDocument.file" class="file-name">
              Selected: {{ newDocument.file.name }} ({{ formatFileSize(newDocument.file.size) }})
            </p>
          </div>
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close">Cancel</button>
        <button class="btn-primary glass-btn-primary" @click="submit" :disabled="!canSubmit || uploading">
          <span v-if="uploading">⏳ Uploading...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from "@/utilities/Requestor"

export default {
  name: 'EditDisputeModal',
  props: {
    visible: { type: Boolean, default: false },
    dispute: { type: Object, default: null }
  },
  data() {
    return {
      debugMode: false,
      uploading: false,
      form: {
        dispute_id: null,
        dispute_type: null,
        credit_card_company: null,
        dispute_company_name: null,
        customer_statement: null,
        issue_type: null,
        resolution: 'pending',
        money_lost: false,
        money_amount: null,
        details: ''
      },
      existingDocuments: [],
      newDocument: {
        title: '',
        document_type: '',
        file: null,
        fileData: null
      },
      documentsToRemove: []
    }
  },
  computed: {
    isFinanceType() {
      const type = this.form.dispute_type
      return type === 'snap_finance' || type === 'finance' || type === 'merchant_service' || type === 'attorney' || type === 'court'
    },
    companyLabel() {
      const labels = {
        'snap_finance': 'Finance Company Name',
        'merchant_service': 'Merchant Service Name',
        'attorney': 'Attorney/Law Firm Name',
        'court': 'Court Name'
      }
      return labels[this.form.dispute_type] || 'Company Name'
    },
    companyPlaceholder() {
      const placeholders = {
        'snap_finance': 'e.g., Snap Finance, Ally Financial',
        'merchant_service': 'e.g., Stripe, PayPal, Square',
        'attorney': 'e.g., Law Offices of John Smith',
        'court': 'e.g., Los Angeles Superior Court'
      }
      return placeholders[this.form.dispute_type] || 'Enter company name'
    },
    canSubmit() {
      if (!this.form.dispute_type) return false
      
      if (this.form.dispute_type === 'credit_card') {
        if (!this.form.credit_card_company) return false
      }
      
      if (this.isFinanceType) {
        if (!this.form.dispute_company_name) return false
      }
      
      if (this.form.dispute_type === 'customer') {
        if (!this.form.customer_statement || !this.form.issue_type) return false
      }
      
      if (this.form.dispute_type !== 'customer' && this.form.dispute_type !== 'credit_card' && !this.isFinanceType) {
        if (!this.form.customer_statement || !this.form.issue_type) return false
      }
      
      if (this.form.money_lost) {
        if (!this.form.money_amount || parseFloat(this.form.money_amount) <= 0) {
          return false
        }
      }
      
      return !this.uploading
    }
  },
  watch: {
    visible: {
      handler(val) {
        if (val && this.dispute) {
          this.$nextTick(() => {
            this.loadDisputeData()
          })
        }
        if (!val) {
          this.resetDocumentState()
        }
      },
      immediate: true
    },
    dispute: {
      handler(newVal) {
        if (this.visible && newVal) {
          this.$nextTick(() => {
            this.loadDisputeData()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    loadDisputeData() {
      console.log('Loading dispute data:', this.dispute)
      
      if (!this.dispute) {
        console.warn('No dispute data available')
        return
      }
      
      const disputeData = { ...this.dispute }
      
      // Handle credit_card company name properly
      let creditCardCompany = disputeData.credit_card_company || null
      let disputeCompanyName = disputeData.dispute_company_name || null
      
      if (disputeData.dispute_type === 'credit_card') {
        creditCardCompany = disputeData.dispute_company_name || disputeData.credit_card_company || null
        disputeCompanyName = null
      }
      
      this.form = {
        dispute_id: disputeData.id || disputeData.dispute_id || null,
        dispute_type: disputeData.dispute_type || null,
        credit_card_company: creditCardCompany,
        dispute_company_name: disputeCompanyName,
        customer_statement: disputeData.customer_statement || null,
        issue_type: disputeData.issue_type || null,
        resolution: disputeData.resolution || 'pending',
        money_lost: disputeData.money_lost === true || disputeData.money_lost === 'true' || false,
        money_amount: disputeData.money_amount ? parseFloat(disputeData.money_amount) : null,
        details: disputeData.details || ''
      }
      
      // Load existing documents
      this.existingDocuments = disputeData.documents ? [...disputeData.documents] : []
      console.log('Existing documents:', this.existingDocuments)
      console.log('Form after loading:', this.form)
    },
    
    resetDocumentState() {
      this.newDocument = {
        title: '',
        document_type: '',
        file: null,
        fileData: null
      }
      this.documentsToRemove = []
      this.uploading = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    
    handleDocumentUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.newDocument.file = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.newDocument.fileData = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    
    async addDocument() {
      if (!this.newDocument.file || !this.newDocument.title) {
        this.$emit('error', 'Please provide a title and select a file')
        return
      }
      
      try {
        const formData = new FormData()
        formData.append('dispute_id', this.form.dispute_id)
        formData.append('title', this.newDocument.title)
        formData.append('document_type', this.newDocument.document_type || 'evidence')
        formData.append('file', this.newDocument.file)
        
        const response = await Requestor.postAsyncP("/api/sales/add_dispute_document/", formData)
        
        if (response.data.error) {
          this.$emit('error', response.data.error)
        } else {
          // Add the new document to the list
          const newDoc = response.data.result
          this.existingDocuments.push(newDoc)
          
          // Reset the new document form
          this.newDocument = {
            title: '',
            document_type: '',
            file: null,
            fileData: null
          }
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
          
          this.$emit('success', 'Document added successfully')
        }
      } catch (error) {
        console.error('Error adding document:', error)
        this.$emit('error', error.response.data.error || 'Error adding document')
      }
    },
    
    removeDocument(index) {
      const doc = this.existingDocuments[index]
      if (doc && doc.id) {
        // Mark for removal (will be deleted on submit or immediately)
        this.documentsToRemove.push(doc.id)
      }
      this.existingDocuments.splice(index, 1)
    },
    
    viewDocument(doc) {
      // Emit event to parent to show document viewer
      this.$emit('view-document', doc)
    },
    
    async deleteRemovedDocuments() {
      if (this.documentsToRemove.length === 0) return
      
      try {
        for (const docId of this.documentsToRemove) {
          await Requestor.postAsyncP("/api/sales/delete_dispute_document/", {
            document_id: docId
          })
        }
        console.log('Removed documents deleted successfully')
      } catch (error) {
        console.error('Error deleting documents:', error)
      }
    },
    
    onDisputeTypeChange() {
      if (this.form.dispute_type !== 'credit_card') {
        this.form.credit_card_company = null
      }
      
      if (this.form.dispute_type === 'credit_card') {
        this.form.dispute_company_name = null
      }
      
      if (!this.isFinanceType && this.form.dispute_type !== 'credit_card') {
        this.form.dispute_company_name = null
      }
    },
    
    async submit() {
      if (!this.canSubmit) return
      
      this.uploading = true
      
      try {
        // First, delete any removed documents
        await this.deleteRemovedDocuments()
        
        const data = {
          dispute_id: this.form.dispute_id,
          dispute_type: this.form.dispute_type,
          customer_statement: this.form.customer_statement,
          issue_type: this.form.issue_type,
          resolution: this.form.resolution,
          money_lost: this.form.money_lost,
          details: this.form.details
        }
        
        if (this.form.dispute_type === 'credit_card') {
          data.credit_card_company = this.form.credit_card_company
          data.dispute_company_name = this.form.credit_card_company
        } else if (this.form.dispute_company_name) {
          data.dispute_company_name = this.form.dispute_company_name
        }
        
        if (this.form.money_lost && this.form.money_amount) {
          data.money_amount = this.form.money_amount
        }
        
        console.log('Submitting edit data:', data)
        
        const response = await Requestor.postAsyncP("/api/sales/edit_dispute/", data)
        
        if (response.data.error) {
          this.$emit('error', response.data.error)
        } else {
          this.$emit('updated')
          this.close()
        }
      } catch (error) {
        console.error('Error updating dispute:', error)
        this.$emit('error', error.response.data.error || 'Error updating dispute')
      } finally {
        this.uploading = false
      }
    },
    
    close() {
      this.resetDocumentState()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.money-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 14px;
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.money-amount-input {
  padding-left: 32px !important;
  font-size: 16px;
  font-weight: 600;
}

.money-amount-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.money-amount-input::-webkit-inner-spin-button,
.money-amount-input::-webkit-outer-spin-button {
  opacity: 0.5;
  height: 30px;
}

.field-hint {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

/* Document Upload Styles */
.document-upload-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
  margin-top: 10px;
}

.documents-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}

.existing-documents {
  margin-bottom: 16px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.doc-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.doc-icon {
  color: #3b82f6;
  font-size: 16px;
}

.doc-name {
  font-weight: 500;
  font-size: 14px;
  color: #1e293b;
}

.doc-type {
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 10px;
  border-radius: 12px;
}

.doc-actions {
  display: flex;
  gap: 6px;
}

.doc-action-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.doc-action-btn:hover {
  background: #e2e8f0;
}

.doc-action-btn.view-btn {
  color: #3b82f6;
}

.doc-action-btn.remove-btn {
  color: #ef4444;
}

.doc-action-btn.remove-btn:hover {
  background: #fee2e2;
}

.document-upload-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.doc-title-input {
  flex: 2;
  min-width: 150px;
}

.doc-type-select {
  flex: 1;
  min-width: 120px;
}

.add-doc-btn {
  padding: 10px 20px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.add-doc-btn:hover:not(:disabled) {
  background: #e2e8f0;
}

.add-doc-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-input {
  flex: 1;
  padding: 8px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
}

.file-name {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px 24px 0 0;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.modal-input,
.modal-select,
.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 10px;
}

.radio-group {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.9);
  color: #475569;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/EditDisputeModal.vue